import React from "react"
import { CounterOffer, formatNaira } from "../negotiation"
import type { NegotiationRequest } from "../../../../components/NegotiationRequest"

interface Props {
    status: string
    // The order's price: the customer's original price until both sides agree,
    // then the agreed price.
    price: string
    counterOffer: CounterOffer | null
    negotiationRequest?: NegotiationRequest | null
    // Only if the API sends it: the pre-negotiation price, once `price` has
    // become the agreed one.
    originalPrice?: string | null
}

const formatTime = (value?: string | null) => {
    if (!value) return ""
    const date = new Date(value.replace(" ", "T"))
    if (Number.isNaN(date.getTime())) return value
    return date.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
}

// Statuses before an agreement. Anything else (accepted, picked up, delivered…)
// means `price` is the agreed price.
const OPEN_STATUSES = new Set(["pending", "counter_sent", "customer_countered"])
const CLOSED_STATUSES = new Set(["rejected", "cancelled"])

const PriceCell = ({ label, value, highlight }: { label: string; value: string | null | undefined; highlight?: string }) => (
    <div className="min-w-0 rounded-lg bg-white border border-gray-100 px-3 py-2">
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className={`text-sm font-semibold tabular-nums ${value ? highlight ?? "text-gray-800" : "text-gray-300"}`}>
            {value ? formatNaira(value) : "—"}
        </p>
    </div>
)

const NegotiationCard: React.FC<Props> = ({ status, price, counterOffer, negotiationRequest, originalPrice }) => {
    const companyOffer = counterOffer?.company_offered_price ?? null
    // The customer's latest price: their counter, else what they proposed when they tapped Negotiate.
    const customerOffer = counterOffer?.customer_offered_price ?? negotiationRequest?.proposed_price ?? null
    const negotiated = !!counterOffer || !!negotiationRequest
    const agreed = !OPEN_STATUSES.has(status) && !CLOSED_STATUSES.has(status)

    // Accepted without any negotiation: the order card already shows the price.
    if (agreed && !negotiated) return null

    let headline: string
    let sub: string | null = null
    let tone = "text-purple-700"
    if (status === "pending" && negotiationRequest) {
        headline = negotiationRequest.proposed_price
            ? `Customer offers ${formatNaira(negotiationRequest.proposed_price)}`
            : "The customer wants to negotiate"
        sub = negotiationRequest.proposed_price
            ? `Original ${formatNaira(price)} → Customer offers ${formatNaira(negotiationRequest.proposed_price)}`
            : `Original price ${formatNaira(price)}`
    } else if (status === "pending") {
        headline = `Customer placed this order at ${formatNaira(price)}`
    } else if (status === "counter_sent") {
        headline = `Waiting for the customer – you offered ${formatNaira(companyOffer)}`
        tone = "text-amber-700"
    } else if (status === "customer_countered") {
        headline = `Customer countered ${formatNaira(counterOffer?.customer_offered_price)}`
        if (companyOffer) headline += ` (you offered ${formatNaira(companyOffer)})`
    } else if (status === "rejected") {
        headline = "You rejected this order"
        tone = "text-red-600"
    } else if (status === "cancelled") {
        headline = "The customer rejected this order"
        tone = "text-red-600"
    } else {
        headline = `Agreed price ${formatNaira(price)}`
        if (originalPrice && Number(originalPrice) !== Number(price)) headline += ` (originally ${formatNaira(originalPrice)})`
        tone = "text-green-700"
    }

    const waitingOnCompany = status === "pending" || status === "customer_countered"
    const messageFrom = counterOffer?.proposed_by === "company" ? "Your message" : "Customer's message"

    // Once agreed, `price` is the agreed amount, so it can't be labelled "original".
    const cells: { label: string; value: string | null | undefined; highlight?: string }[] = agreed
        ? [
            ...(originalPrice ? [{ label: "Original price", value: originalPrice }] : []),
            { label: "Agreed price", value: price, highlight: "text-green-700" },
        ]
        : [{ label: "Original price", value: price }]
    cells.push(
        { label: "Your offer", value: companyOffer, highlight: "text-[#143887]" },
        { label: "Customer's offer", value: customerOffer, highlight: "text-purple-700" }
    )
    const gridCols = cells.length === 4 ? "sm:grid-cols-4" : "sm:grid-cols-3"

    return (
        <div className={`rounded-lg border p-4 ${waitingOnCompany ? "border-purple-200 bg-purple-50/40" : "border-gray-100 bg-gray-50"}`}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wide">Price Negotiation</p>
                {waitingOnCompany && (
                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-[11px] font-semibold text-purple-700">
                        Your turn
                    </span>
                )}
            </div>

            <p className={`text-sm font-semibold ${tone}`}>{headline}</p>
            {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}

            {negotiated && (
                <>
                    <div className={`mt-3 grid grid-cols-1 ${gridCols} gap-2`}>
                        {cells.map((cell) => (
                            <PriceCell key={cell.label} {...cell} />
                        ))}
                    </div>

                    {counterOffer?.description && (
                        <div className="mt-3">
                            <p className="text-[11px] text-gray-400 mb-1">{messageFrom}</p>
                            <p className="max-w-prose rounded-lg rounded-tl-none border border-gray-100 bg-white px-3 py-2 text-xs text-gray-600 whitespace-pre-line break-words">
                                “{counterOffer.description}”
                            </p>
                        </div>
                    )}

                    {counterOffer?.updated_at && (
                        <p className="mt-2 text-[11px] text-gray-400">
                            Last offer by {counterOffer.proposed_by === "company" ? "you" : "the customer"} · {formatTime(counterOffer.updated_at)}
                        </p>
                    )}
                </>
            )}
        </div>
    )
}

export default NegotiationCard
